import { useEffect, useState } from 'react';
import { getMisafirMesajlar, mesajOkundu, deleteMisafirMesaj } from '../../services/api';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import OnayPopup from '../../components/OnayPopup';

function formatTarih(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

export default function MisafirMesajlari() {
  const [mesajlar, setMesajlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [silOnay, setSilOnay] = useState(null);
  const [sadeceokunmamis, setSadeceokunmamis] = useState(false);
  const [acikMesaj, setAcikMesaj] = useState(null);

  const yukle = async (okunmamis = sadeceokunmamis) => {
    setYukleniyor(true);
    try {
      const data = await getMisafirMesajlar(okunmamis);
      setMesajlar(data || []);
    } finally {
      setYukleniyor(false);
    }
  };

  useEffect(() => { yukle(); }, []);

  const okunduIsaretle = async (id) => {
    await mesajOkundu(id);
    setMesajlar(prev => prev.map(m => m.misafirMesajId === id ? { ...m, okunduMu: true } : m));
    if (acikMesaj?.misafirMesajId === id) setAcikMesaj(prev => ({ ...prev, okunduMu: true }));
  };

  const sil = async (id) => {
    await deleteMisafirMesaj(id);
    setMesajlar(prev => prev.filter(m => m.misafirMesajId !== id));
    if (acikMesaj?.misafirMesajId === id) setAcikMesaj(null);
    setSilOnay(null);
  };

  const filtrelendi = sadeceokunmamis ? mesajlar.filter(m => !m.okunduMu) : mesajlar;
  const okunmamisSayisi = mesajlar.filter(m => !m.okunduMu).length;

  return (
    <div className="p-4 sm:p-6">
      <OnayPopup
        acik={silOnay !== null}
        mesaj="Bu mesajı silmek istediğinize emin misiniz?"
        onEvet={() => sil(silOnay)}
        onHayir={() => setSilOnay(null)}
      />
      <PageHeader
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
        title="Misafir Mesajları"
        subtitle="Web sitesinden gelen iletişim formları"
        right={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={sadeceokunmamis}
                onChange={e => { setSadeceokunmamis(e.target.checked); yukle(e.target.checked); }}
                className="rounded" />
              Sadece okunmamış
              {okunmamisSayisi > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold">
                  {okunmamisSayisi}
                </span>
              )}
            </label>
          </div>
        }
      />

      {yukleniyor ? <LoadingSpinner /> : filtrelendi.length === 0 ? (
        <EmptyState text={sadeceokunmamis ? 'Okunmamış mesaj yok.' : 'Henüz mesaj gelmemiş.'} />
      ) : (
        <div className="space-y-3 mt-4">
          {filtrelendi.map(m => (
            <div key={m.misafirMesajId}
              className={`bg-white rounded-xl border shadow-sm transition-all ${!m.okunduMu ? 'border-blue-200 shadow-blue-50' : 'border-gray-100'}`}>
              <div className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                {/* Sol: bilgiler */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {!m.okunduMu && (
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    )}
                    <span className="font-semibold text-gray-900 text-sm">{m.adSoyad}</span>
                    <span className="text-gray-400 text-xs">·</span>
                    <a href={`tel:${m.telefon}`} className="text-sm text-blue-600 hover:underline">{m.telefon}</a>
                    {m.email && (
                      <>
                        <span className="text-gray-400 text-xs">·</span>
                        <a href={`mailto:${m.email}`} className="text-sm text-blue-600 hover:underline truncate max-w-[180px]">{m.email}</a>
                      </>
                    )}
                    <span className="text-xs text-gray-400 ml-auto">{formatTarih(m.olusturmaTarihi)}</span>
                  </div>
                  <p className={`text-sm text-gray-600 leading-relaxed line-clamp-2 ${acikMesaj?.misafirMesajId === m.misafirMesajId ? 'line-clamp-none' : ''}`}>
                    {m.mesaj}
                  </p>
                  {m.mesaj.length > 120 && (
                    <button onClick={() => {
                      setAcikMesaj(prev => prev?.misafirMesajId === m.misafirMesajId ? null : m);
                      if (!m.okunduMu) okunduIsaretle(m.misafirMesajId);
                    }}
                      className="text-xs text-blue-500 hover:underline mt-1">
                      {acikMesaj?.misafirMesajId === m.misafirMesajId ? 'Daha az göster' : 'Tamamını gör'}
                    </button>
                  )}
                </div>

                {/* Sağ: aksiyonlar */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!m.okunduMu && (
                    <button onClick={() => okunduIsaretle(m.misafirMesajId)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors">
                      Okundu
                    </button>
                  )}
                  <button onClick={() => setSilOnay(m.misafirMesajId)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-medium transition-colors">
                    Sil
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
